import { Tag } from '@blueprintjs/core';
import { mdiFilter, mdiLinkVariant, mdiMapOutline, mdiUpdate } from '@mdi/js';
import Icon from '@mdi/react';
import classNames from 'classnames';
import React, { FunctionComponent, ReactNode } from 'react';
import { t } from 'ttag';

import { LayerMetadata } from 'uf-api';
import { hasVisibleParent } from 'uf/layers/metadata';
import rootStyles from 'uf/styles/root.module.css';
import textStyles from 'uf/styles/text.module.css';
import { CopyableText } from 'uf/ui/base/CopyableText/CopyableText';
import { IconSizeNew } from 'uf/ui/base/icon';
import WithLayerMetadata from 'uf/ui/layers/hocs/withLayerMetadata';
import Source from 'uf/ui/layers/LayerMetadataSummary/Source';
import useUserFlag from 'uf/ui/user/useUserFlag';
import FavoriteLayerStar from './FavoriteLayerStar';

import localStyles from './LayerMetadataSummary.module.css';
import Flex from 'uf/ui/base/Flex';
import { useDispatch } from 'react-redux';
import { searchUpdated } from 'uf/projects/layerManagerSlice';

const devLayerMetadataClassName = classNames(
  'h5',
  localStyles.layerDescriptionLineHeight,
  rootStyles.breakWord,
  textStyles.mutedText,
);

const sourceLinkClassName = classNames('h4', textStyles.activeColor);

interface Props {
  isLayerFavoritable: boolean;
  layerMetadata: LayerMetadata;
  displayPanelView: boolean;
}

export const LayerHeader: FunctionComponent<Props> = ({
  isLayerFavoritable,
  layerMetadata,
  displayPanelView,
}) => {
  const dispatch = useDispatch();
  const hasDevLayerMetadataExtrasFlag = useUserFlag(
    'dev-layer-metadata-extras',
  );
  const {
    source,
    description,
    display_hints: displayHints,
    key,
    namespace,
    layer_type: layerType,
    name,
    supply_pattern: supplyPattern,
    full_path: fullPath,
    geometry_type_key: geometryTypeKey,
  } = layerMetadata;
  const hasSource = source && (source.url || source.name);
  const sourceLink: ReactNode = hasSource ? (
    <span className={sourceLinkClassName}>
      <Source source={source} />
    </span>
  ) : null;
  const hasParent = hasVisibleParent(layerMetadata);
  return (
    <>
      {!displayPanelView && (
        <Flex alignItems="center">
          {isLayerFavoritable && (
            <FavoriteLayerStar
              layerKey={key}
              layerNamespace={namespace}
              layerType={layerType}
            />
          )}
          <h2>{name}</h2>
        </Flex>
      )}
      <Flex
        flexDirection={displayPanelView ? 'col-reverse' : 'row'}
        style={{ width: '-webkit-fill-available', marginBottom: 12 }}>
        <Flex flexDirection="col" style={{ flex: 3 }}>
          {!displayPanelView && displayHints.search_tags && (
            <Flex wrap style={{ marginBottom: 8 }}>
              {displayHints.search_tags.map((tag: string) => (
                <Tag
                  key={tag}
                  interactive
                  minimal
                  intent={'success'}
                  className={rootStyles.veryThinMargin}
                  // eslint-disable-next-line no-restricted-syntax
                  onClick={() => dispatch(searchUpdated(tag))}>
                  {tag}
                </Tag>
              ))}
            </Flex>
          )}
          {hasDevLayerMetadataExtrasFlag && (
            <Flex flexDirection="col" className={rootStyles.thinMarginBottom}>
              {supplyPattern && (
                <span className={devLayerMetadataClassName}>
                  Supply pattern: <CopyableText text={supplyPattern} />
                </span>
              )}
              <span className={devLayerMetadataClassName}>
                Full path: <CopyableText text={fullPath} />
              </span>
              <span className={devLayerMetadataClassName}>
                Geometry type key: {geometryTypeKey}
              </span>
              <span className={devLayerMetadataClassName}>
                Vintage: {displayHints.vintage}
              </span>
              <span className={devLayerMetadataClassName}>
                Layer type: {layerType}
              </span>
            </Flex>
          )}
          {description ? (
            <p style={{ fontSize: 14 }}>{description}</p>
          ) : (
            <small>{t`No description provided by source`}</small>
          )}
        </Flex>
        <Flex
          flexDirection="col"
          style={displayPanelView ? {} : { marginLeft: 'auto', flex: 1 }}>
          <LayerProp
            label={t`Source`}
            value={sourceLink}
            iconPath={mdiLinkVariant}
          />
          <LayerProp
            label={t`Scale`}
            value={displayHints.jurisdictional_scale}
            iconPath={mdiMapOutline}
          />
          <LayerProp
            label={t`Last updated from source`}
            unavailableLabel={t`Unavailable from source`}
            value={displayHints.as_of_date}
            iconPath={mdiUpdate}
          />
          {hasParent && (
            <LayerProp
              label={t`Filtered from layer`}
              value={
                <WithLayerMetadata layerId={displayHints.parent.full_path}>
                  {({ layerMetadata: parentLayerMetadata }) =>
                    parentLayerMetadata ? parentLayerMetadata.name : null
                  }
                </WithLayerMetadata>
              }
              iconPath={mdiFilter}
            />
          )}
        </Flex>
      </Flex>
    </>
  );
};

interface LayerPropProps {
  label: string;
  unavailableLabel?: string;
  value: ReactNode;
  iconPath: string;
}

const layerPropIconClassName = classNames(
  rootStyles.veryThinMarginTop,
  textStyles.mutedText,
);
const LayerProp: FunctionComponent<LayerPropProps> = ({
  label,
  unavailableLabel = t`Unavailable`,
  value,
  iconPath,
}) => {
  return (
    <Flex className={rootStyles.smallMarginBottom}>
      <Icon
        className={layerPropIconClassName}
        path={iconPath}
        size={IconSizeNew.SMALL}
      />
      <Flex
        flexDirection="col"
        className={classNames(
          rootStyles.thinMarginLeft,
          rootStyles.veryThinMarginTop,
        )}>
        <span
          className={classNames(textStyles.mutedText, textStyles.fontSizeH5)}>
          {label}
        </span>
        <span className="h4">
          {value}
          {!value && <small>{unavailableLabel}</small>}
        </span>
      </Flex>
    </Flex>
  );
};
